import {
  Box,
  Button,
  Switch,
  Typography,
  IconButton,
  Drawer,
  Divider,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { X } from '@phosphor-icons/react';
import { FC, useEffect, useState } from 'react';

import { updateUserHideSectionsApi } from '../../utils/apis';
import theme from '../../theme';
import { useUser } from '../../hooks/useUser';
import { HideSectionsConfig } from '../../schemas/apis/interface';

interface HideActionConfigProps {
  open: boolean;
  onClose: () => void;
}

const HideActionConfig: FC<HideActionConfigProps> = ({ open, onClose }) => {
  const queryClient = useQueryClient();
  const { user } = useUser();

  const [hideSections, setHideSections] = useState<HideSectionsConfig>({
    order_management: false,
    reservation_management: false,
    store_management: false,
    brand_management: false,
    agent_analytics: false,
    agents: false,
    knowledge_base: false,
    voice_library: false,
    phone_numbers: false,
    whatsapp_chats: false,
    quibble_workflows: true,
    restaurant: false,
    ai_agent: false,
    billing: false,
    call_logs: false,
    manage_organization: false,
    campaign: false,
  });

  useEffect(() => {
    if (user?.hideSections) {
      setHideSections(user.hideSections);
    }
  }, [user]);

  const {
    mutateAsync: updateHideSections,
    isPending: isUpdateHideSectionsPending,
  } = useMutation({
    mutationFn: (values: HideSectionsConfig) =>
      updateUserHideSectionsApi(values),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user'] });
      onClose();
    },
    onError: (error: any) => {
      console.log(error);
    },
  });

  const handleSaveConfig = () => {
    updateHideSections(hideSections);
  };

  const handleToggleChange = (section: keyof HideSectionsConfig) => {
    setHideSections(prev => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const sectionConfigs = [
    {
      key: 'order_management' as keyof HideSectionsConfig,
      label: 'Order Management',
      description: 'Hide order management features',
    },
    {
      key: 'reservation_management' as keyof HideSectionsConfig,
      label: 'Reservation Management',
      description: 'Hide reservation management features',
    },
    {
      key: 'store_management' as keyof HideSectionsConfig,
      label: 'Outlet Management',
      description: 'Hide outlet management features',
    },
    {
      key: 'brand_management' as keyof HideSectionsConfig,
      label: 'Brand Management',
      description: 'Hide brand management features',
    },
    {
      key: 'agent_analytics' as keyof HideSectionsConfig,
      label: 'Agent Analytics',
      description: 'Hide agent analytics features',
    },
    {
      key: 'agents' as keyof HideSectionsConfig,
      label: 'Agents',
      description: 'Hide agents features',
    },
    {
      key: 'knowledge_base' as keyof HideSectionsConfig,
      label: 'Knowledge Base',
      description: 'Hide knowledge base features',
    },
    {
      key: 'voice_library' as keyof HideSectionsConfig,
      label: 'Voice Library',
      description: 'Hide voice library features',
    },
    {
      key: 'phone_numbers' as keyof HideSectionsConfig,
      label: 'Phone Numbers',
      description: 'Hide phone numbers features',
    },

    {
      key: 'whatsapp_chats' as keyof HideSectionsConfig,
      label: 'WhatsApp Chats',
      description: 'Hide WhatsApp chats features',
    },
    {
      key: 'quibble_workflows' as keyof HideSectionsConfig,
      label: 'Workflows',
      description: 'Hide workflows features',
    },
  ];

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      slotProps={{
        paper: {
          sx: {
            width: '637px',
            p: 0,
            '&::-webkit-scrollbar': {
              display: 'none',
            },
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            display: 'flex',
            flexDirection: 'column',
          },
        },
      }}
    >
      {/* Sticky Header Section */}
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 1,
          backgroundColor: '#FFFFFF',
          px: 2,
          pt: 2,
        }}
      >
        {/* Header Section */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 2,
          }}
        >
          {/* Close Button and Title */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <IconButton
              onClick={onClose}
              size="small"
              sx={{
                '&:hover': { backgroundColor: '#E8EBF1' },
                transition: 'all 0.2s ease',
              }}
            >
              <X size={18} />
            </IconButton>
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: 18,
                color: 'custom.textBlue',
                letterSpacing: '-0.2px',
              }}
            >
              Hide Sections Configuration
            </Typography>
          </Box>

          {/* Save Button */}
          <Button
            size="small"
            variant="contained"
            color="primary"
            onClick={handleSaveConfig}
            disabled={isUpdateHideSectionsPending}
            sx={{
              fontSize: 14,
              fontWeight: 500,
              borderRadius: '6px',
              textTransform: 'none',
              boxShadow: 'none',
              transition: 'all 0.3s ease',
              '&:hover': {
                boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
              },
            }}
          >
            {isUpdateHideSectionsPending ? 'Updating...' : 'Save Config'}
          </Button>
        </Box>

        <Divider
          sx={{
            borderColor: 'rgba(0, 0, 0, 0.08)',
            mb: 1,
            mx: -2,
          }}
        />
      </Box>

      {/* Scrollable Content Area */}
      <Box
        sx={{
          overflow: 'auto',
          flexGrow: 1,
          px: 2,
          pb: 2,
          '&::-webkit-scrollbar': {
            display: 'none',
          },
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
        }}
      >
        <Typography
          variant="body2"
          sx={{
            fontSize: '14px',
            color: theme.palette.text.secondary,
            mb: 2,
          }}
        >
          Configure which sections should be hidden from the dashboard
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {sectionConfigs.map(section => (
            <Box
              key={section.key}
              sx={{
                p: 2,
                border: '1px solid #DCE0E7',
                borderRadius: '8px',
                bgcolor: '#FFFFFF',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                transition: 'all 0.2s ease',
                '&:hover': {
                  borderColor: '#6366F1',
                  boxShadow: '0px 2px 8px rgba(99, 102, 241, 0.1)',
                },
              }}
            >
              <Box sx={{ flex: 1 }}>
                <Typography
                  sx={{
                    fontSize: '14px',
                    fontWeight: 500,
                    color: '#2B2D3F',
                    mb: 0.5,
                  }}
                >
                  {section.label}
                </Typography>
                <Typography
                  sx={{
                    fontSize: '12px',
                    color: theme.palette.text.secondary,
                  }}
                >
                  {section.description}
                </Typography>
              </Box>
              <Switch
                checked={hideSections[section.key] || false}
                onChange={() => handleToggleChange(section.key)}
                color="primary"
                size="small"
                sx={{
                  width: 36,
                  height: 20,
                  padding: 0,
                  '& .MuiSwitch-switchBase': {
                    padding: 0,
                    margin: '2px',
                    transitionDuration: '300ms',
                    '&.Mui-checked': {
                      transform: 'translateX(16px)',
                      color: '#fff',
                      '& + .MuiSwitch-track': {
                        backgroundColor: '#6366F1',
                        opacity: 1,
                        border: 0,
                      },
                    },
                    '&.Mui-focusVisible .MuiSwitch-thumb': {
                      color: '#6366F1',
                      border: '6px solid #fff',
                    },
                  },
                  '& .MuiSwitch-thumb': {
                    boxSizing: 'border-box',
                    width: 16,
                    height: 16,
                  },
                  '& .MuiSwitch-track': {
                    borderRadius: 26 / 2,
                    backgroundColor: '#E9E9EA',
                    opacity: 1,
                  },
                }}
              />
            </Box>
          ))}
        </Box>
      </Box>
    </Drawer>
  );
};

export default HideActionConfig;
